import { createSwiper } from '@nulo/swiper';

/**
 * @extends {HTMLElement}
 */
class BlogArticleContent extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {import('swiper').Swiper[]} */
  #swipers = [];

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const toggle = this.querySelector('[data-hero-product-toggle]');
    const panel = this.querySelector('[data-hero-product-panel]');
    const toggleLabel = toggle?.querySelector('[data-hero-product-toggle-label]');

    if (toggle instanceof HTMLButtonElement && panel instanceof HTMLElement) {
      const cartIcon = toggle.querySelector('.blog-article-content__hero-toggle-icon--cart');
      const closeIcon = toggle.querySelector('.blog-article-content__hero-toggle-icon--close');

      toggle.addEventListener(
        'click',
        () => {
          const isOpen = toggle.getAttribute('aria-expanded') === 'true';
          const nextOpen = !isOpen;

          toggle.setAttribute('aria-expanded', String(nextOpen));
          panel.hidden = !nextOpen;

          if (toggleLabel instanceof HTMLElement) {
            toggleLabel.textContent = nextOpen
              ? this.dataset.hideProductLabel || 'Hide product'
              : this.dataset.showProductLabel || 'Show product';
          }

          toggle.setAttribute(
            'aria-label',
            nextOpen
              ? this.dataset.hideProductAria || 'Hide product details'
              : this.dataset.showProductAria || 'Show product details'
          );

          cartIcon?.classList.toggle('visually-hidden', nextOpen);
          closeIcon?.classList.toggle('visually-hidden', !nextOpen);
          this.classList.toggle('blog-article-content--product-open', nextOpen);
        },
        { signal }
      );
    }

    for (const table of this.querySelectorAll('.blog-table')) {
      if (!(table instanceof HTMLElement)) continue;
      table.addEventListener(
        'scroll',
        () => {
          table.classList.toggle('is-scrolled', table.scrollLeft > 0);
        },
        { passive: true, signal }
      );
    }

    this.#initImageGridSwipers();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#destroySwipers();
    this._initialized = false;
  }

  #initImageGridSwipers() {
    for (const region of this.querySelectorAll('.blog-article-image-grid--slider')) {
      if (!(region instanceof HTMLElement)) continue;

      const swiperEl = region.querySelector('[data-swiper]');
      if (!(swiperEl instanceof HTMLElement)) continue;

      const swiper = createSwiper({
        region,
        el: swiperEl,
        activeSlideClass: 'swiper-slide-active',
        config: {
          slidesPerView: 1,
          navigation: {
            nextEl: region.querySelector('.blog-article-image-grid__nav--next'),
            prevEl: region.querySelector('.blog-article-image-grid__nav--prev'),
          },
        },
      });

      if (swiper) this.#swipers.push(swiper);
    }
  }

  #destroySwipers() {
    for (const swiper of this.#swipers) {
      swiper?.destroy?.(true, true);
    }
    this.#swipers = [];
  }
}

if (!customElements.get('blog-article-content')) {
  customElements.define('blog-article-content', BlogArticleContent);
}
